import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import useLogout from "../Hooks/useLogout";
import useAuth from "../Hooks/auth/useAuth";
import Loader from "../Components/Loader";
import axiosDotnet from "../services/axiosDotnet";
import AIAssistant from "./AIAssistant";
import ExportMenu from '../Components/ExportMenu';
import timeAgo from "../services/timeAgo";
import { forgeAssistant } from "../services/groqService";
import AddProjectMemberModal from "../Components/AddProjectMemberModal";
import CreateTaskModal from "../Components/CreateTaskModal";
import KanbanBoard from '../Components/KanbanBoard';
import NotificationBell from '../Components/NotificationBell';
import { showError, showSuccess, showWarning } from "../services/reactToastify";
// import { formatDistanceToNow } from 'date-fns';

export default function Workspace() {
    const [showDescription, setShowDescription] = useState(false);
    const [showKanban, setShowKanban] = useState(false);
    const [selectedProjectForKanban, setSelectedProjectForKanban] = useState(null);
    // const [projectTasks, setProjectTasks] = useState({});
    // const [expandedProject, setExpandedProject] = useState(null);
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
    const [selectedProjectForTask, setSelectedProjectForTask] = useState(null);
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedProjectForMembers, setSelectedProjectForMembers] = useState(null);
    const [projectMembers, setProjectMembers] = useState([]);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [members, setMembers] = useState([]);
    const [currentWorkSpace, setCurrentWorkSpace] = useState();
    const [error, setError] = useState('');
    const [projectMemberCounts, setProjectMemberCounts] = useState({});
    // const [avgEng, setAvgEng] = useState(0);
    const cwId = useParams().wId;
    const navigate = useNavigate()
    const { auth } = useAuth();
    const Logout = useLogout();

    const handleStartMeeting = () => {
        window.open('https://meet.google.com/new', '_blank');
    };

    // ================= AI Integrate ===============>

    const [AISuggestion, setAISuggestion] = useState('');

    useEffect(() => {

        const projectPromptTemplate = `
Task:
You are an expert Workspace , Project , Tasks and Technical related queries solver and innovative suggestions and solution adviser.
So give a small 1 line suggestion to users for this below Workspace and projects inside it but that l1 line suggestion should be innovative , creative, specific and practically possible & easy.

**Workspace Name:** ${currentWorkSpace?.name}
**Workspace Description:** ${currentWorkSpace?.description}
**Projects inside this Workspace:** ${projects?.map(project => `${project?.title} - ${project?.description} ,\n`)}

Important Rules:
- Do NOT repeat or copy ideas from previous responses.
- The AI Suggestion must be **related** to the workspace and projects but **NOT a copy** of it.
- Be creative and specific.
- Max 20-25 words , strictly not more than that.

JSON Format strict:-
{
"suggestion": "....."
}
`;

        const randomSeed = Math.random().toString(36).substring(2, 10);

        const finalPrompt = projectPromptTemplate + `\n\nGenerate a completely new idea or suggestion. Random seed: ${randomSeed}`

        const handleAsk = async () => {
            if (!finalPrompt.trim()) return;

            setLoading(true);
            try {
                const result = await forgeAssistant(finalPrompt);

                // console.log("Raw response from Groq:", result);
                setAISuggestion(JSON.parse(result)?.suggestion || result);

            } catch (err) {
                setAISuggestion("Sorry, AI is currently unavailable.");
            } finally {
                setLoading(false);
            }
        }

        handleAsk()

    }, [currentWorkSpace])

    // ================= AI Integrate ===============>

    // Example usage
    // const formatted = formatDistanceToNow(new Date("2026-04-29T19:21:30.908111Z"), { addSuffix: true });

    useEffect(() => {
        const fetchCWorkspace = async () => {
            setLoading(true);
            try {
                const response = await axiosDotnet.get('/api/workspaces');
                const cws = response?.data?.filter(w => w?.id == cwId)[0]
                // Check if current user is approved member

                // console.log('Workspace fetched:',);
                setCurrentWorkSpace(cws)
            } catch (err) {
                console.error(err?.response);
                setError(err?.response?.data?.message || 'Failed to fetch workspaces');
            } finally {
                setLoading(false);
            }
        };

        if (auth?.foundUser?.id) {
            fetchCWorkspace();
        }

    }, [auth?.foundUser?.id])

    const fetchProjectMembers = async (projectId) => {
        try {
            const res = await axiosDotnet.get(`/api/projects/${projectId}/members`);
            // console.log(res?.data)
            setProjectMembers(res?.data);
            setSelectedProjectForMembers(projectId);
        } catch (err) {
            showError("Failed to load project members");
        }
    };

    const handleRemoveMember = async (userIdToRemove, userName) => {
        if (!window.confirm(`Remove ${userName} from workspace?`)) return;

        try {
            await axiosDotnet.delete(`/api/workspaces/${cwId}/members/${userIdToRemove}`);
            showSuccess(`${userName} has been removed from the workspace`);
            fetchMembers(); // Refresh list
        } catch (err) {
            showError("Failed to remove member");
        }
    };

    const handleDeleteProject = async (projectId, projectTitle) => {
        if (!window.confirm(`Delete project "${projectTitle}" and all its tasks?`)) return;
    
        try {
            await axiosDotnet.delete(`/api/projects/${projectId}`);
            showSuccess("Project deleted successfully");
            fetchProjects(); // Refresh list
        } catch (err) {
            showError("Failed to delete project");
        }
    };

    const fetchAllProjectMemberCounts = async (projectsList) => {
        try {
            const memberCounts = {};

            await Promise.all(
                projectsList.map(async (project) => {
                    try {
                        const res = await axiosDotnet.get(
                            `/api/projects/${project.id}/members`
                        );

                        memberCounts[project.id] = res.data?.length || 0;
                    } catch (err) {
                        memberCounts[project.id] = 0;
                    }
                })
            );

            setProjectMemberCounts(memberCounts);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const response = await axiosDotnet.get(`/api/projects/${cwId}`);
            // console.log('Project fetched:', response?.data);
            const projectsData = response?.data || [];

            setProjects(projectsData);

            await fetchAllProjectMemberCounts(projectsData);
        } catch (err) {
            console.error(err?.response);
            setError(err?.response?.data?.message || 'Failed to fetch workspaces');
        } finally {
            setLoading(false);
        }
    };

    const fetchMembers = async () => {
        setLoading(true);
        try {
            const response = await axiosDotnet.get(`/api/workspaces/${cwId}/members`);
            // console.log('Members fetched:', response?.data);
            setMembers(response?.data);
        } catch (err) {
            console.error(err?.response);
            setError(err?.response?.data?.message || 'Failed to fetch workspaces');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        if (auth?.foundUser?.id) {
            fetchProjects();
            fetchMembers();
        }

    }, [currentWorkSpace])

    // console.log(currentWorkSpace)

    const calculateAIUsage = () => {
        if (!projects || projects.length === 0) return 68; 
    
        const totalProjects = projects.length;
        const totalTasks = projects.reduce((sum, p) => sum + (p.taskCount || 0), 0);
    
        let usage = Math.min(95, Math.max(45, 
            Math.round((totalProjects * 8) + (totalTasks * 1.2))
        ));
    
        return usage;
    };

    const workspace = {
        name: currentWorkSpace?.name,
        description: currentWorkSpace?.description 
    };

    const metrics = [
        {
            title: "Total \nProjects",
            value: currentWorkSpace?.projectsCount,
            color: "text-emerald-400",
        },
        {
            title: "Tasks \nCreated",
            value: currentWorkSpace?.tasksCount,
            color: "text-violet-400",
        },
        {
            title: "Total \nMembers",
            value: currentWorkSpace?.membersCount,
            color: "text-violet-400",
        },
        {
            title: "AI \nUsage",
            value: "68%", //calculateAIUsage(),
            color: "text-amber-400"
        },
    ];

    const recentProjects = projects.slice(-3);

    const kanbanData = {
        columns: [
            {
                id: "todo",
                title: "To Do",
                count: 7,
                color: "bg-blue-500",
                tasks: [
                    {
                        id: 1,
                        title: "Implement .NET Workspace CRUD",
                        priority: "High",
                        assignee: "MK",
                        due: "Apr 22",
                    },
                    {
                        id: 2,
                        title: "Add real-time Socket.io updates",
                        priority: "Medium",
                        assignee: "SJ",
                        due: "Apr 23",
                    },
                    {
                        id: 3,
                        title: "AI task auto-prioritization logic",
                        priority: "High",
                        assignee: "AI",
                        due: "Apr 20",
                    },
                ],
            },
            {
                id: "progress",
                title: "In Progress",
                count: 5,
                color: "bg-amber-500",
                tasks: [
                    {
                        id: 4,
                        title: "Build Kanban drag & drop board",
                        priority: "High",
                        assignee: "MK",
                        due: "Apr 19",
                    },
                    {
                        id: 5,
                        title: "Forge Assistant sidebar chat UI",
                        priority: "Medium",
                        assignee: "SJ",
                        due: "Apr 21",
                    },
                ],
            },
            {
                id: "done",
                title: "Done",
                count: 12,
                color: "bg-emerald-500",
                tasks: [
                    {
                        id: 6,
                        title: "Spring Boot Auth service",
                        priority: "Done",
                        assignee: "MK",
                        due: "Apr 15",
                    },
                    {
                        id: 7,
                        title: "React folder structure + routing",
                        priority: "Done",
                        assignee: "SJ",
                        due: "Apr 16",
                    },
                ],
            },
        ],
    };

    const handleApprove = async (userId, approve) => {
        // if (!alert(`Are you sure you want to ${approve ? 'approve' : 'reject'} this member?`)) return;

        try {
            await axiosDotnet.post(`/api/workspaces/${cwId}/members/${userId}/approve?approve=${approve}`);
            // Refresh members list
            fetchMembers();
            showSuccess(`Member ${approve ? 'approved' : 'rejected'} successfully!`);
        } catch (err) {
            showError("Failed to update member status");
        }
    };

    // console.log(projectMemberCounts)
    let avgEng = 0
    return (
        <>
            {!currentWorkSpace || loading ? (
                <Loader text="Loading Your Workspace..." />
            ) : (
                <div id="main-content" className="bg-zinc-950 text-white font-sans min-h-screen">
                    {/* Navbar - kept exactly the same style */}
                    <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-lg border-b border-zinc-800">
                        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3 cursor-pointer">
                                <div className="w-8 h-8 bg-violet-900 rounded-xl flex items-center justify-center text-white font-bold text-xl">A</div>
                                <span className="text-2xl font-semibold tracking-tight"><Link to='/dashboard'>AetherForge-Workspace</Link></span>
                            </div>

                            {/* Workspace switcher + user area */}
                            <div className="flex items-center gap-6">
                                <div className="px-4 py-2 bg-zinc-900 rounded-3xl flex items-center gap-2 text-sm border border-zinc-700">
                                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                                    {workspace?.name}
                                </div>
                                <Link to={`/createProject/${cwId}`}>
                                    <button className="px-5 cursor-pointer py-2 bg-violet-900 hover:bg-violet-600 transition-all rounded-3xl font-semibold flex items-center gap-2">
                                        New Project
                                        <span className="text-lg leading-none">+</span>
                                    </button>
                                </Link>
                                <NotificationBell userId={auth?.foundUser?.id} />
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-zinc-700 rounded-2xl flex items-center justify-center text-xs font-bold">{auth?.foundUser?.name[0]}</div>
                                    <div>
                                        <div className="text-sm font-medium">{auth?.foundUser?.name}</div>
                                        <div className="text-[10px] text-zinc-500 -mt-1">{currentWorkSpace?.roleInWorkspace}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </nav>

                    <div className="pt-20 flex h-screen">
                        {/* Sidebar - Workspace details + navigation */}
                        <div className="w-72 bg-zinc-900 border-r border-zinc-800 flex flex-col">
                            {/* Workspace header inside sidebar */}
                            <Link to='/dashboard'>
                                <button className="mt-2 ml-2 cursor-pointer px-5 py-1 bg-zinc-800 hover:bg-zinc-700 rounded-2xl">
                                    ←
                                </button>
                            </Link>
                            <div className="px-6 pt-2 pb-6 border-b border-zinc-800">

                                <h1 className="text-3xl font-bold tracking-tight">{workspace.name}</h1>
                                <button className="mt-1 cursor-pointer text-md text-emerald-400" onClick={() => setShowDescription(!showDescription)}> {showDescription ? "Hide Description" : "Show Description"}</button>
                                {showDescription && <p className="text-zinc-400 text-sm mt-2 leading-tight">
                                    {workspace.description}
                                </p>}
                            </div>

                            {/* Navigation */}
                            <div className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
                                <div
                                    onClick={() => setActiveTab("dashboard")}
                                    className={`flex items-center gap-3 px-4 py-4 rounded-3xl cursor-pointer transition-all ${activeTab === "dashboard"
                                        ? "bg-violet-900 text-white shadow-inner"
                                        : "hover:bg-zinc-800 text-zinc-300"
                                        }`}
                                >
                                    <span className="text-xl">📊</span>
                                    <span className="font-medium">Dashboard</span>
                                </div>

                                <div
                                    onClick={() => setActiveTab("projects")}
                                    className={`flex items-center gap-3 px-4 py-4 rounded-3xl cursor-pointer transition-all ${activeTab === "projects"
                                        ? "bg-violet-900 text-white shadow-inner"
                                        : "hover:bg-zinc-800 text-zinc-300"
                                        }`}
                                >
                                    <span className="text-xl">📋</span>
                                    <span className="font-medium">Projects</span>
                                </div>

                                {/* <div
                        onClick={() => setActiveTab("kanban")}
                        className={`flex items-center gap-3 px-4 py-4 rounded-3xl cursor-pointer transition-all ${activeTab === "kanban"
                            ? "bg-violet-900 text-white shadow-inner"
                            : "hover:bg-zinc-800 text-zinc-300"
                            }`}
                    >
                        <span className="text-xl">📌</span>
                        <span className="font-medium">Kanban Board</span>
                        <span className="ml-auto text-[10px] bg-violet-800 px-2 py-px rounded-full">AI Sprint</span>
                    </div> */}

                                <div
                                    onClick={() => setActiveTab("aiAssistant")}
                                    className={`flex items-center gap-3 px-4 py-4 rounded-3xl cursor-pointer transition-all ${activeTab === "aiAssistant"
                                        ? "bg-violet-900 text-white shadow-inner"
                                        : "hover:bg-zinc-800 text-zinc-300"
                                        }`}
                                >
                                    <span className="text-xl">🤖</span>
                                    <span className="font-medium">AI Forge Assistant</span>
                                </div>

                                {/* <div
                        onClick={() => setActiveTab("notifications")}
                        className={`flex items-center gap-3 px-4 py-4 rounded-3xl cursor-pointer transition-all ${activeTab === "notifications"
                            ? "bg-violet-900 text-white shadow-inner"
                            : "hover:bg-zinc-800 text-zinc-300"
                            }`}
                    >
                        <span className="text-xl">💬</span>
                        <span className="font-medium">Notifications</span>
                    </div> */}

                                <div
                                    onClick={() => setActiveTab("members")}
                                    className={`flex items-center gap-3 px-4 py-4 rounded-3xl cursor-pointer transition-all ${activeTab === "members"
                                        ? "bg-violet-900 text-white shadow-inner"
                                        : "hover:bg-zinc-800 text-zinc-300"
                                        }`}
                                >
                                    <span className="text-xl">👥</span>
                                    <span className="font-medium">Members </span>
                                </div>

                                {/* <div className="flex items-center gap-3 px-4 py-4 rounded-3xl cursor-pointer hover:bg-zinc-800 text-zinc-300">
                        <span className="text-xl">📈</span>
                        <span className="font-medium">Analytics</span>
                    </div> */}
                            </div>

                            {/* Footer of sidebar - Dark/Light toggle + premium feel */}
                            <div className="p-6 border-t border-zinc-800 text-xs flex items-center justify-between">
                                <div className="flex items-center gap-2 text-zinc-400">
                                    <span className="px-3 py-1 bg-zinc-800 rounded-3xl">🌙 Dark</span>
                                </div>
                                <div className="text-violet-400 font-medium flex items-center gap-1">
                                    <span>Powered by</span>
                                    <span className="text-xl">A</span>
                                </div>
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className="flex-1 overflow-auto">
                            {/* Top bar inside main area */}
                            <div className="px-8 py-2 border-b border-zinc-800 flex items-center justify-between bg-zinc-950 sticky top-0 z-40">
                                <div className="flex items-center gap-4">
                                    <h4 className="text-2xl font-semibold tracking-tighter">
                                        {activeTab === "dashboard" && "Workspace Dashboard"}
                                        {activeTab === "projects" && "All Projects"}
                                        {activeTab === "kanban" && "Sprint Kanban Board"}
                                        {activeTab === "aiAssistant" && "🔥 AetherForge AI Assistant"}
                                        {activeTab === "members" && "👥 All Members"}
                                        {activeTab === "notifications" && "💬 All Notifications"}
                                    </h4>
                                    {activeTab === "kanban" && (
                                        <div className="text-sm px-4 py-1 bg-emerald-900 text-emerald-300 rounded-3xl flex items-center gap-1">
                                            <span className="animate-pulse">●</span>
                                            AI auto-prioritizing 3 tasks
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-3 relative">
                                    {/* <NotificationBell userId={auth?.foundUser?.id} /> */}
                                    <button
                                        onClick={() => setShowExportMenu(!showExportMenu)}
                                        className="px-6 cursor-pointer py-3 border border-zinc-700 hover:border-zinc-400 rounded-3xl text-sm font-medium transition-all flex items-center gap-2"
                                    >
                                        📤 Export Report
                                    </button>

                                    <button
                                        onClick={handleStartMeeting}
                                        className="px-6 cursor-pointer py-3 bg-violet-600 hover:bg-violet-500 rounded-3xl font-semibold transition-all flex items-center gap-2"
                                    >
                                        📹 Start Meeting
                                    </button>

                                    {showExportMenu && <ExportMenu onClose={() => setShowExportMenu(false)} />}
                                </div>

                            </div>

                            {/* DASHBOARD TAB */}
                            {activeTab === "dashboard" && (
                                <div className="p-8">
                                    {/* Metrics grid */}
                                    <div className="grid grid-cols-4 gap-6">
                                        {metrics.map((m, i) => (
                                            <div
                                                key={i}
                                                className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 hover:border-violet-500 transition-all group"
                                            >
                                                <div className="flex justify-between items-center">
                                                    <p className="text-zinc-400 text-sm font-medium">{m.title}</p>
                                                    <span className={`text-4xl font-bold ${m.color}`}>{m.value}</span>
                                                </div>
                                                {/* <p className="text-xs text-zinc-400 mt-6 group-hover:text-violet-400 transition-colors">
                                        {m.sub}
                                    </p> */}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Visualization section */}

                                    <div className="mt-12 grid grid-cols-12 gap-6">
                                        <div className="col-span-8 bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
                                            <div className="flex justify-between mb-8">
                                                <h3 className="font-semibold text-xl">Team Engagement Per Project Analytics</h3>
                                                <div className="text-sm text-zinc-400 gap-6">
                                                    <span className="flex items-center gap-1">
                                                        <span className="w-3 h-px bg-violet-400"></span> Engagement
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <span className="w-3 h-px bg-amber-400"></span> Project
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="relative h-[340px] w-full flex items-end gap-4 border-b border-zinc-700 pb-8">
                                                {projects && projects?.length > 0 ? (
                                                    projects.slice(0, 10).map((project, i) => {
                                                        // Calculate Team Engagement = (members * tasks) normalized to percentage
                                                        // const members = project.membersCount || 5;           //Wroong code dont have .memberscount fallback
                                                        const members = projectMemberCounts[project.id] || 0;
                                                        const tasks = project.taskCount || project.tasksCount || 8;

                                                        const rawScore = members * tasks;

                                                        console.log(members, tasks, rawScore)

                                                        const maxRawScore = Math.max(
                                                            ...projects.map(p => {
                                                                const m =
                                                                    projectMemberCounts[p.id] || 0;

                                                                const t =
                                                                    p.taskCount ||
                                                                    p.tasksCount ||
                                                                    0;

                                                                return m * t;
                                                            }),
                                                            1
                                                        );

                                                        const engagementPercent =
                                                            Math.round((rawScore / maxRawScore) * 100);
                                                        // Math.round((rawScore / (25*30)) * 100);

                                                        const finalHeight =
                                                            Math.max(
                                                                10,
                                                                Math.min(100, engagementPercent)
                                                            );

                                                        avgEng = avgEng + finalHeight
                                                        // setAvgEng(avgEng+finalHeight);

                                                        // const maxPossible = 25 * 30; 
                                                        // let engagementPercent = Math.round((rawScore / maxPossible) * 100);
                                                        // const finalHeight = Math.max(35, Math.min(98, engagementPercent));

                                                        return (
                                                            <div
                                                                key={project.id || i}
                                                                className="flex-1 flex flex-col min-w-[30px] h-full justify-end items-center gap-2 group"
                                                            >
                                                                {/* <div key={project.id || i} className="flex-1 flex flex-col min-w-[30px] justify-end items-center gap-2 group"> */}
                                                                <div className="w-full h-full flex items-end justify-center">
                                                                    <div
                                                                        className="w-[10px] bg-gradient-to-t from-violet-500 via-violet-400 to-violet-300 
                                                        rounded-t-2xl transition-all duration-300 hover:from-violet-600 hover:to-violet-400"
                                                                        style={{ height: `${finalHeight}%` }}
                                                                    ></div>
                                                                </div>
                                                                <div className="text-[10px] font-bold text-center text-zinc-400 mt-2 font-medium">
                                                                    {finalHeight} %
                                                                </div>
                                                                <div className="text-[10px] text-center text-zinc-400 mt-2 font-medium">
                                                                    {project.title?.length > 8
                                                                        ? project.title.substring(0, 8) + "..."
                                                                        : project.title || `P${i + 1}`}
                                                                    {/* {["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8", "P9", "P10"][i]} */}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    // Fallback if no projects
                                                    [45, 62, 78, 85, 91, 92, 88, 95, 56, 30].map((height, i) => (
                                                        <div key={i} className="flex-1 flex flex-col min-w-[30px] justify-end items-center gap-2 group">
                                                            <div
                                                                className="w-full bg-gradient-to-t from-violet-500 via-violet-400 to-violet-300 rounded-t-2xl transition-all duration-300 hover:from-violet-600 hover:to-violet-400"
                                                                style={{ height: `${height}%` }}
                                                            ></div>
                                                            <div className="text-[10px] text-center text-zinc-400 mt-2 font-medium">
                                                                {["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8", "P9", "P10"][i]}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            <div className="text-center text-xs text-zinc-500 mt-6">
                                                Last 10 Project Engagement
                                            </div>
                                        </div>

                                        <div className="col-span-4 space-y-6">
                                            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 h-1/2 flex flex-col justify-center text-center">
                                                <div className="text-7xl font-bold text-emerald-400">{(avgEng / Math.min(10, projects?.length)) || 0}%</div>
                                                <p className="text-zinc-400 mt-2">Average Team Engagement</p>
                                            </div>

                                            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 flex-1 flex items-center gap-4">
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">Next AI Suggestion</div>
                                                    <p className="text-zinc-400 text-sm mt-1">
                                                        {AISuggestion}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => setActiveTab("aiAssistant")}
                                                    className="px-5 cursor-pointer py-3 text-xs bg-violet-900 rounded-3xl whitespace-nowrap">
                                                    Ask AI
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recent Projects (as per overview) */}
                                    <div className="mt-12">
                                        <div className="flex justify-between items-baseline mb-6">
                                            <h3 className="text-xl font-semibold">Recent Projects</h3>
                                            <button onClick={() => setActiveTab("projects")} className="text-violet-400 cursor-pointer text-sm flex items-center gap-1 hover:underline">
                                                View all projects →
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-3 gap-6">
                                            {recentProjects?.map((project) => (
                                                <div
                                                    key={project.id}
                                                    className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 hover:border-violet-500 transition-all"
                                                >
                                                    <div className="flex justify-between">
                                                        <h4 className="font-semibold">{project.title}</h4>
                                                        <span
                                                            className={`text-xs font-bold ${project.status === "Done"
                                                                ? " text-amber-500"
                                                                : project.status === "Review"
                                                                    ? "text-violet-500"
                                                                    : "text-emerald-500"
                                                                }`}
                                                        >
                                                            {project.status}
                                                        </span>
                                                    </div>
                                                    <div className="mt-6 h-2 bg-zinc-800 rounded-3xl overflow-hidden">
                                                        <div
                                                            className="h-2 bg-violet-500 rounded-3xl"
                                                            style={{ width: `${project.progress}%` }}
                                                        ></div>
                                                    </div>
                                                    <div className="flex justify-between text-xs mt-3 text-zinc-400">
                                                        <span>{project.taskCount} tasks</span>
                                                        {/* <span>{project.members} members</span> */}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PROJECTS TAB - simple list view */}
                            {activeTab === "projects" && (
                                <div className="p-8">
                                    <div className="text-xl font-medium mb-6">
                                        {projects.length == 0 && <button
                                            className="text-violet-400 "
                                        >
                                            No Projects To Show
                                        </button>}
                                        {/*  */}
                                        {/* All Projects in this workspace */}
                                    </div>

                                    <div className="space-y-4">
                                        {projects.map((p) => (
                                            <div
                                                key={p.id}
                                                className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-8 py-6 rounded-3xl hover:border-violet-500"
                                            >
                                                <div className="flex items-center gap-6">
                                                    <div className="w-12 h-12 bg-zinc-800 rounded-2xl flex items-center justify-center text-3xl">📦</div>
                                                    <div>
                                                        <div className="font-semibold">{p.title}</div>
                                                        <div className="text-zinc-400 text-sm">
                                                            {/* {p?.taskCount || 0} tasks */}
                                                            {/* • {p.members} members */}

                                                            <button
                                                                // onClick={() => fetchTasksForProject(p.id)}
                                                                onClick={() => {
                                                                    setSelectedProjectForKanban(p.id);
                                                                    setShowKanban(true);
                                                                }}
                                                                className="px-5 py-2 hover:text-white text-violet-400 hover:bg-violet-500 text-sm ml-5 cursor-pointer border-zinc-600 rounded-2xl transition-all"
                                                            >
                                                                {/* {expandedProject === p.id ? "Hide Tasks" : "Show Tasks"} */}
                                                                📋 Tasks
                                                            </button>

                                                            <button
                                                                onClick={() => fetchProjectMembers(p.id)}
                                                                className="cursor-pointer ml-5 mt-1 px-5 py-2 text-sm border-violet-500 text-violet-400 hover:bg-violet-500 hover:text-white rounded-2xl transition-all"
                                                            >
                                                                👥 Members
                                                            </button>
                                                            {/* Update & Delete (for Project Admins) */}
                                                            {(p.roleInProject === "ADMIN" || true) && (   // Change condition as per your logic
                                                                <>
                                                                    <button
                                                                        onClick={() => navigate(`/updateProject/${cwId}/${p.id}`)}
                                                                        className="px-5 py-2 text-sm cursor-pointer hover:bg-zinc-800 rounded-2xl transition-all"
                                                                    >
                                                                        ✏️ Update
                                                                    </button>

                                                                    <button
                                                                        onClick={() => handleDeleteProject(p.id, p.title)}
                                                                        className="px-5 py-2 text-sm cursor-pointer hover:bg-red-900/30 text-red-400 rounded-2xl transition-all"
                                                                    >
                                                                        🗑️ Delete
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>

                                                    </div>
                                                </div>
                                                {true && (<div className="flex items-center gap-3">

                                                    <button
                                                        onClick={() => {
                                                            setSelectedProjectForTask(p.id);
                                                            setShowCreateTaskModal(true);
                                                        }}
                                                        className="cursor-pointer px-5 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl transition-all"
                                                    >
                                                        + New Task
                                                    </button>

                                                    <button
                                                        onClick={() => {

                                                            setSelectedProjectId(p.id);
                                                            setShowAddMemberModal(true);
                                                        }}
                                                        className="cursor-pointer px-5 py-2 text-sm border border-violet-500 text-violet-400 hover:bg-violet-500 hover:text-white rounded-2xl transition-all"
                                                    >
                                                        + Add Member
                                                    </button>


                                                </div>)}

                                                

                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* KANBAN TAB - full implementation with columns */}
                            {activeTab === "kanban" && (
                                <div className="p-8">
                                    <div className="flex justify-between items-center mb-8">
                                        {/* <h2 className="text-2xl font-semibold">Sprint Kanban Board</h2> */}
                                        <div className="flex items-center gap-2 text-sm bg-zinc-900 px-5 py-2 rounded-3xl border border-zinc-700">
                                            AI auto-prioritization enabled
                                            <span className="text-emerald-400">●</span>
                                        </div>
                                    </div>

                                    <div className="flex gap-6 overflow-x-auto pb-8">
                                        {kanbanData.columns.map((column) => (
                                            <div
                                                key={column.id}
                                                className="min-w-[340px] bg-zinc-900 border border-zinc-800 rounded-3xl p-5 flex flex-col"
                                            >
                                                {/* Column header */}
                                                <div className="flex items-center justify-between mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-3 h-3 rounded-full ${column.color}`}></div>
                                                        <h4 className="font-semibold text-lg">{column.title}</h4>
                                                    </div>
                                                    <div className="bg-zinc-800 text-zinc-400 text-xs px-3 py-1 rounded-3xl">
                                                        {column.count}
                                                    </div>
                                                </div>

                                                {/* Tasks */}
                                                <div className="flex-1 space-y-3">
                                                    {column.tasks.map((task) => (
                                                        <div
                                                            key={task.id}
                                                            className="bg-zinc-800 rounded-3xl p-5 hover:shadow-xl transition-all cursor-grab active:cursor-grabbing"
                                                        >
                                                            <div className="font-medium leading-tight">{task.title}</div>

                                                            <div className="flex justify-between mt-8">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-7 h-7 bg-violet-900 text-white text-[10px] font-bold flex items-center justify-center rounded-2xl">
                                                                        {task.assignee}
                                                                    </div>
                                                                    <span className="text-xs text-zinc-400">due {task.due}</span>
                                                                </div>

                                                                <span
                                                                    className={`text-xs font-medium px-4 flex items-center rounded-3xl ${task.priority === "High"
                                                                        ? "bg-red-900 text-red-300"
                                                                        : "bg-amber-900 text-amber-300"
                                                                        }`}
                                                                >
                                                                    {task.priority}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Add task button */}
                                                <button className="mt-6 w-full py-4 text-sm font-medium border border-dashed border-zinc-600 rounded-3xl hover:border-violet-400 transition-colors">
                                                    + Add task
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* AI TAB - simple list view */}
                            {activeTab === "aiAssistant" && (
                                <div className="p-8">
                                    {/* <div className="text-xl font-medium mb-6">AI Assistance To Make Your Tasks Easy !</div> */}
                                    {/* Give project UniquetitleName and Description for IT Tech Task or Projects and also give its tools and Techs required for it */}
                                    <div className="space-y-4">
                                        <AIAssistant />
                                    </div>
                                </div>
                            )}
                            {/* MEMBERS TAB - simple list view */}
                            {activeTab === "members" && (
                                <div className="p-8">
                                    {/* <div className="text-xl font-medium mb-6">All Projects in this workspace</div> */}
                                    <div className="space-y-4">
                                        {members?.map((p, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-8 py-6 rounded-3xl hover:border-violet-500"
                                            >
                                                <div className="flex items-center gap-6">
                                                    <div className="w-12 h-12 bg-zinc-800 rounded-2xl flex items-center justify-center text-3xl">{idx + 1}</div>
                                                    <div>
                                                        <div className="font-semibold">{p.name}</div>
                                                        <div className="text-zinc-400 text-sm">{p?.email}
                                                            {/* • {p.members} members */}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="text-right">

                           
                                                    <div className="text-right flex items-center justify-end gap-3">
                                                        {p.roleInWorkspace !== "ADMIN" && (
                                                            <>
                                                                {currentWorkSpace?.roleInWorkspace === "ADMIN" && (
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-md text-zinc-400 font-medium">Status:</span>

                                                                        {p.status === "PENDING" ? (
                                                                            <div className="flex gap-2">
                                                                                {/* Approve Button */}
                                                                                <button
                                                                                    onClick={() => handleApprove(p.userId, true)}
                                                                                    className="px-4 cursor-pointer py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-2xl transition-all flex items-center gap-1"
                                                                                >
                                                                                    <span>✅</span> Approve
                                                                                </button>

                                                                                {/* Reject Button */}
                                                                                <button
                                                                                    onClick={() => handleApprove(p.userId, false)}
                                                                                    className="px-4 cursor-pointer py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-2xl transition-all flex items-center gap-1"
                                                                                >
                                                                                    <span>✕</span> Reject
                                                                                </button>
                                                                            </div>
                                                                        ) : p.status === "APPROVED" ? (
                                                                            <span className="px-4 py-1.5 bg-emerald-900/50 text-emerald-400 text-sm font-medium rounded-2xl border border-emerald-700">
                                                                                ✓ Approved
                                                                            </span>
                                                                        ) : (
                                                                            <span className="px-4 py-1.5 bg-red-900/50 text-red-400 text-sm font-medium rounded-2xl border border-red-700">
                                                                                ✕ Rejected
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                    </div>

                                                </div>
                                                {/* Remove Button - Only for Admins (except self) */}
                                                {currentWorkSpace?.roleInWorkspace === "ADMIN" &&
                                                    p.roleInWorkspace !== "ADMIN" &&
                                                    p.userId !== auth?.foundUser?.id && (
                                                        <button
                                                            onClick={() => handleRemoveMember(p.userId, p.name)}
                                                            className="px-5 cursor-pointer py-2 text-sm border border-red-600 hover:bg-red-900/30 text-red-400 rounded-2xl transition-all flex items-center gap-2"
                                                        >
                                                            🗑️ Remove
                                                        </button>
                                                    )}
                                                <div className="flex items-center gap-2">
                                                    {/* <span className="text-md text-zinc-400 font-medium">Role:</span> */}

                                                    {p.roleInWorkspace === "ADMIN" ? <div className="font-medium text-red-600">👑 {p.roleInWorkspace}</div> :
                                                        <div className="font-medium text-emerald-400">{p.roleInWorkspace}</div>
                                                    }
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                    {showAddMemberModal && (
                        <AddProjectMemberModal
                            projectId={selectedProjectId}
                            workspaceId={cwId}
                            isOpen={showAddMemberModal}
                            onClose={() => setShowAddMemberModal(false)}
                            onMemberAdded={() => {
                                // Optional: Refresh projects or show success
                                fetchProjects();
                            }}
                        />
                    )}

                    {showCreateTaskModal && (
                        <CreateTaskModal
                            projects={projects}
                            workspace={currentWorkSpace}
                            projectId={selectedProjectForTask}
                            isOpen={showCreateTaskModal}
                            onClose={() => setShowCreateTaskModal(false)}
                            onTaskCreated={() => {
                                fetchProjects(); // Refresh project list
                            }}
                        />
                    )}

                    {/* ==================== KANBAN MODAL ==================== */}
                    {/* {showKanban && selectedProjectForKanban && (
            <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-zinc-800">
                    <h2 className="text-2xl font-semibold">Kanban Board</h2>
                    <button
                        onClick={() => setShowKanban(false)}
                        className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-2xl"
                    >
                        Close
                    </button>
                </div>

                <KanbanBoard projectId={selectedProjectForKanban} />
            </div>
        )} */}

                    {showKanban && selectedProjectForKanban && (
                        <div className="fixed inset-0 bg-black/95 z-[60] overflow-auto">
                            <div className="p-6">
                                <button
                                    onClick={() => setShowKanban(false)}
                                    className="mb-6 cursor-pointer px-6 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-2xl"
                                >
                                    ← Back
                                </button>
                                <KanbanBoard
                                    projectId={selectedProjectForKanban}
                                    workspaceId={cwId}
                                    setSelectedProjectForTask={setSelectedProjectForTask}
                                    setShowCreateTaskModal={setShowCreateTaskModal}
                                />
                            </div>
                        </div>
                    )}
                    {selectedProjectForMembers && (
                        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                            <div className="bg-zinc-900 border border-zinc-700 rounded-3xl w-full max-w-lg p-8 max-h-[80vh] overflow-auto">
                                <h2 className="text-2xl font-semibold mb-6">Project Members : {projectMembers?.length}</h2>

                                <div className="space-y-3">
                                    {projectMembers.map((m) => (
                                        <div key={m.userId} className="flex justify-between items-center bg-zinc-800 p-4 rounded-2xl">
                                            <div>
                                                <div className="font-medium">{m.name}</div>
                                                <div className="text-sm text-zinc-400">{m.email}</div>
                                            </div>
                                            <div className={`px-4 py-1 rounded-2xl text-sm font-medium ${m.roleInProject === "ADMIN" ? "bg-violet-900 text-violet-300" : "bg-zinc-700 text-zinc-300"
                                                }`}>
                                                {m.roleInProject}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={() => setSelectedProjectForMembers(null)}
                                    className="mt-6 cursor-pointer w-full py-3 border border-zinc-600 rounded-2xl hover:bg-zinc-800"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>

    )
}